import React from 'react';
import { useOwnerProps, useGetGlobalAttributes, useGetXstyledProps } from '@elliemae/ds-props-helpers';
import { styled } from '@elliemae/ds-system';
import type { TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { DSFloatingWrapperSlots, DSFloatingWrapperName } from '../constants/index.js';
export interface PopoverArrowT
  extends TypescriptHelpersT.PropsForGlobalOnSlots<typeof DSFloatingWrapperName, typeof DSFloatingWrapperSlots> {
  placement: string;
  style: React.CSSProperties;
  arrowElementRef?: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
}
const arrowWidth = 18;
const OFFSET_FIX_SHADOW_DEFECT = 0.25;

const arrowDimensionBase13 = arrowWidth / 13;
const OFFSET_FIX_SHADOW_DEFECT_BASE13 = OFFSET_FIX_SHADOW_DEFECT / 13;
const arrowDimensionBase16 = arrowWidth / 16;
const OFFSET_FIX_SHADOW_DEFECT_BASE16 = OFFSET_FIX_SHADOW_DEFECT / 16;

const arrowPlacementBase13 = arrowDimensionBase13 - OFFSET_FIX_SHADOW_DEFECT_BASE13;
const arrowPlacementBase16 = arrowDimensionBase16 - OFFSET_FIX_SHADOW_DEFECT_BASE16;

const isTopOrBottom = (placement: string) => placement.startsWith('top') || placement.startsWith('bottom');

const isLeftOrRight = (placement: string) => placement.startsWith('left') || placement.startsWith('right');

const StyledArrow = styled('div', {
  name: DSFloatingWrapperName,
  slot: DSFloatingWrapperSlots.ARROW,
})<{ 'data-placement': string }>`
  line-height: 0;
  position: absolute;
  width: ${arrowDimensionBase16}rem;
  height: ${arrowDimensionBase16}rem;
  pointer-events: none;
  background-color: transparent;
  & .stroke {
    fill: rgb(105, 116, 137);
    fill-opacity: 0.4;
  }
  & .fill {
    fill: rgb(255, 255, 255);
  }

  &[data-placement^='top'] {
    svg {
      transform: rotateZ(180deg);
    }
    bottom: -${arrowPlacementBase16}rem;
    left: ${(props) => {
      if (props['data-placement'].endsWith('start')) return `25%`;
      if (props['data-placement'].endsWith('end')) return `75%`;
      return '';
    }};
  }
  &[data-placement^='right'] {
    svg {
      transform: rotateZ(-90deg);
    }
    left: -${arrowPlacementBase16}rem;
  }
  &[data-placement^='bottom'] {
    top: -${arrowPlacementBase16}rem;
    left: ${(props) => {
      if (props['data-placement'].endsWith('start')) return `25%`;
      if (props['data-placement'].endsWith('end')) return `75%`;
      return '';
    }};
  }
  &[data-placement^='left'] {
    svg {
      transform: rotateZ(90deg);
    }
    right: -${arrowPlacementBase16}rem;
  }
  margin-left: ${(props) => (isTopOrBottom(props['data-placement']) ? `-${arrowDimensionBase16 / 2}rem` : '0')};
  margin-top: ${(props) => (isLeftOrRight(props['data-placement']) ? `-${arrowDimensionBase16 / 2}rem` : '0')};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}) {
    width: ${arrowDimensionBase13}rem;
    height: ${arrowDimensionBase13}rem;

    &[data-placement^='top'] {
      bottom: -${arrowPlacementBase13}rem;
    }

    &[data-placement^='bottom'] {
      top: -${arrowPlacementBase13}rem;
    }

    &[data-placement^='left'] {
      right: -${arrowPlacementBase13}rem;
    }

    &[data-placement^='right'] {
      left: -${arrowPlacementBase13}rem;
    }

    margin-left: ${(props) => (isTopOrBottom(props['data-placement']) ? `-${arrowDimensionBase13 / 2}rem` : '0')};

    margin-top: ${(props) => (isLeftOrRight(props['data-placement']) ? `-${arrowDimensionBase13 / 2}rem` : '0')};
  }
`;

const StylePathShadow = styled('path', {
  name: DSFloatingWrapperName,
  slot: DSFloatingWrapperSlots.ARROW_SHADOW,
})``;

const StylePathFill = styled('path', {
  name: DSFloatingWrapperName,
  slot: DSFloatingWrapperSlots.ARROW_FILL,
})``;

export const PopoverArrow = ({ placement, style, arrowElementRef, ...rest }: PopoverArrowT): React.ReactElement => {
  const ownerProps = useOwnerProps(rest);
  const globalAttributes = useGetGlobalAttributes(rest);
  const xstyledProps = useGetXstyledProps(rest);
  return (
    <StyledArrow
      key="popper-arrow"
      data-placement={placement}
      style={style}
      innerRef={arrowElementRef}
      data-testid="ds-tooltip-arrow"
      aria-hidden="true"
      {...ownerProps}
      {...globalAttributes}
      {...xstyledProps}
    >
      <svg viewBox="0 0 30 30">
        <StylePathShadow
          {...ownerProps}
          className="stroke"
          d="M23.7,27.1L17,19.9C16.5,19.3,15.8,19,15,19s-1.6,0.3-2.1,0.9l-6.6,7.2C5.3,28.1,3.4,29,2,29h26
        C26.7,29,24.6,28.1,23.7,27.1z"
        />
        <StylePathFill
          {...ownerProps}
          className="fill"
          d="M23,27.8c1.1,1.2,3.4,2.2,5,2.2h2H0h2c1.7,0,3.9-1,5-2.2l6.6-7.2c0.7-0.8,2-0.8,2.7,0L23,27.8L23,27.8z"
        />
      </svg>
    </StyledArrow>
  );
};
